import { useEffect, useMemo } from "react";

import { shouldOptionBeShown } from "../../../helpers/conditions.ts";
import { useVehicleSelection } from "../../../providers/VehicleSelectionContext.tsx";
import { ComplexBundle } from "../../../types/Product.ts";
import DropdownLarge from "../../elements/DropdownLarge.tsx";

interface Props {
  step: ComplexBundle.Step;
  stepIndex: number;
  entry: ComplexBundle.Entry;
  entryIndex: number;
}

export const FreeTextDropdown = ({ stepIndex, entry, entryIndex }: Props) => {
  const { selectedOptions, handleOptionChange, validationErrors } =
    useVehicleSelection();
  const hasError = validationErrors.some(
    (err) => err.stepIndex === stepIndex && err.entryIndex === entryIndex,
  );
  const options = useMemo(() => {
    const filteredOptions = Array.from(entry.products.entries())
      .filter(([, option]) => shouldOptionBeShown(option, selectedOptions))
      .map(([optionIndex, option]) => {
        return {
          label: option.option_name,
          value: optionIndex,
        };
      });

    // Sort options in ascending order
    return filteredOptions.sort((a, b) => {
      // Try to parse as numbers for numeric sorting (e.g., years)
      const aNum = Number(a.label);
      const bNum = Number(b.label);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum; // Numeric ascending sort
      }

      // Fallback to alphabetical sort
      return a.label.localeCompare(b.label);
    });
  }, [entry.products, selectedOptions]);

  const entryKey = `${stepIndex}-${entryIndex}`;

  useEffect(() => {
    const currentlySelected = selectedOptions[entryKey] ?? null;

    if (currentlySelected === null) {
      return;
    }

    if (!options.find((opt) => opt.value === currentlySelected)) {
      handleOptionChange(stepIndex, entryIndex, null);
    }
  }, [
    handleOptionChange,
    entryIndex,
    options,
    selectedOptions,
    stepIndex,
    entryKey,
  ]);

  return (
    <div>
      <DropdownLarge
        options={options}
        label={entry.entry_name}
        selected={(selectedOptions[entryKey] as number | undefined) ?? null}
        required={!!entry.entry_required}
        onChange={(value) => {
          handleOptionChange(
            stepIndex,
            entryIndex,
            value === null ? null : Number(value),
          );
        }}
        error={hasError}
      />
      {entry.entry_description && (
        <div
          className="text-sm px-1 pt-1"
          dangerouslySetInnerHTML={{ __html: entry.entry_description }}
        />
      )}
    </div>
  );
};
